function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

/* eslint no-use-before-define: 0 */
const dateFns = require('date-fns');

function _call(body, then, direct) {
  if (direct) {
    return then ? then(body()) : body();
  }

  try {
    var result = Promise.resolve(body());
    return then ? result.then(then) : result;
  } catch (e) {
    return Promise.reject(e);
  }
}

const {
  DateTime
} = require('luxon');

function _invoke(body, then) {
  var result = body();

  if (result && result.then) {
    return result.then(then);
  }

  return then(result);
}

const _ = require('lodash');

const Stats = require('../entities').Stats;

const AccessContext = require('./contexts/access-context');

const jstz = require('jstimezonedetect');

const StatsUnique = require('../entities/statsUnique');

const breakdownFields = 'events.sum,gender.breakdown.ageRange.sum,ageRange(sum,breakdown.gender.sum)';
const fields = [`totals.reach(${breakdownFields})`, `totals.response(${breakdownFields})`, `totals.action(${breakdownFields})`, 'intervals(intervalStart,reach.events.sum,response.events.sum,action.events.sum)', 'restrictions(name,value,group)'];
const loyaltyFields = [`totals.reach(${breakdownFields})`, `totals.response(${breakdownFields})`, `totals.action(${breakdownFields})`, `totals.earned(${breakdownFields})`, 'restrictions(name,value,group)'];
const uniquesFields = ['count', 'restrictions(name,value,group)'];
const parse = Stats.fromJson;

const fixTimeSeries = (rangeStart, rangeEnd, granularity, unit = 'day') => {
  const startDate = DateTime.fromISO(rangeStart);
  const endDate = DateTime.fromISO(rangeEnd);
  const intervalCount = unit === 'month' ? endDate.diff(startDate, 'months').months / granularity : endDate.diff(startDate, 'days').days / granularity;
  return (stats
  /*: Stats*/
  ) => {
    const timeSeries = stats.timeSeries; // Don't fill in holes in a null list

    if (!timeSeries) return stats;
    const mergedTimeSeries = timeSeries.slice();

    for (let d = 0; d < intervalCount; d += 1) {
      const newTimestamp = unit === 'month' ? startDate.plus({
        months: d * granularity
      }).toMillis() : startDate.plus({
        days: d * granularity
      }).toMillis();
      mergedTimeSeries.push({
        timeStamp: newTimestamp,
        reach: 0,
        response: 0,
        action: 0
      });
    }

    const newTimeSeries = _.chain(mergedTimeSeries).uniqBy('timeStamp').sortBy('timeStamp').value();

    return Object.assign(stats, {
      timeSeries: newTimeSeries
    });
  };
};

class StatsRepo {
  constructor(client
  /*: ApiClient*/
  , context
  /*: AccessContext*/
  , offerId
  /*: ?string*/
  ) {
    this.client = client;
    this.context = context;
    this.offerId = offerId;
  }

  buildPath(context
  /*: ?Object*/
  )
  /*: string*/
  {
    const {
      propertyId,
      businessId,
      locationId
    } = context || this.context.serialize();
    const offerId = (context || this).offerId;
    return [`/properties/${propertyId}`, businessId ? `/businesses/${businessId}` : '', locationId ? `/locations/${locationId}` : '', offerId ? `/offers/${offerId}` : '', '/stats'].join('');
  }

  canAccessUnrestricted(isLoyalty
  /*: ?boolean*/
  )
  /*: boolean*/
  {
    const {
      propertyId,
      businessId,
      locationId
    } = this.context.serialize();
    const offerId = this.offerId;
    const permission = [propertyId ? 'property' : null, businessId ? 'business' : null, locationId ? 'location' : null, offerId ? 'offer' : null, 'stats', isLoyalty ? 'loyalty' : null, 'unrestricted'].filter(Boolean).join('.');
    return this.client.access.checkPermissions(this.context.serialize(), [`${permission}.key1`, `${permission}.key2`]);
  }

  get({
    /*: {
        rangeStart: isoDateString,
        rangeEnd: isoDateString,
        granularity?: number,
        timezone: ?string,
        unit?: string,
        context?: Object
      }*/

    rangeStart,
    rangeEnd,
    granularity = 1,
    timezone,
    unit = 'day',
    context
  })
  /*: Promise<Stats>*/
  {
    const _this = this;

    return _call(function () {
      return _await(_this.canAccessUnrestricted(), function (canAccessUnrestricted) {
        const path = `${_this.buildPath(context)}${canAccessUnrestricted ? '/unrestricted' : ''}`;
        const params = {
          rangeStart,
          rangeEnd,
          granularity,
          timezone,
          unit
        };
        const req = new _this.client.Request(path, params, fields);
        req.parse = parse;
        return _this.client.get(req).then(stats => fixTimeSeries(rangeStart, rangeEnd, granularity, unit)(stats));
      });
    });
  }

  hasAllLocations({
    /*: {
        propertyId?: string, businessId?: string, offerId?: string
      }*/

    propertyId,
    businessId,
    offerId
  })
  /*: Promise<boolean>*/
  {
    const _this2 = this;

    return _call(function () {
      const perms = offerId ? ['property.business.offer.stats.get'] : ['property.business.stats.get'];
      return _await(_this2.client.access.checkPermissions({
        propertyId,
        businessId
      }, perms));
    });
  }

  getDateRange({
    /*: {
        startDate: string,
        endDate: string,
        granularity?: number,
        unit?: string
      }*/

    startDate,
    endDate,
    granularity,
    unit = 'day'
  }) {
    const _this3 = this;

    return _call(function () {
      let _exit = false;

      const {
        propertyId,
        businessId,
        locationId
      } = _this3.context.serialize();

      const offerId = _this3.offerId;
      const timezone
      /*: ?string*/
      = getUserTimezone();
      const rangeStart = getExplicitDateCrossover(startDate, unit);
      const rangeEnd = getExplicitDateCrossover(endDate, unit);
      return _await(_invoke(function () {
        if (!locationId && businessId) {
          return _await(_this3.hasAllLocations({
            propertyId,
            businessId,
            offerId
          }), function (hasAllLocations) {
            return function () {
              if (!hasAllLocations) {
                return _await(_this3.client.properties.for(propertyId).businesses.for(businessId).locations.search({
                  hasRoles: true,
                  accessContext: 'implicit'
                }), function (myLocations) {
                  return _await(Promise.all(myLocations.items.map(eachLocation => _this3.get({
                    rangeStart,
                    rangeEnd,
                    timezone,
                    granularity,
                    unit,
                    context: {
                      propertyId,
                      businessId,
                      locationId: eachLocation.id,
                      offerId
                    }
                  }).catch(() => null))), function (allStats) {
                    // Ignore any accesses we have for locations that don't exist =\
                    _exit = true;
                    return allStats.filter(Boolean).reduce((acc, value) => value.add(acc));
                  });
                });
              }
            }();
          });
        }
      }, function (_result) {
        return _exit ? _result : _this3.get({
          rangeStart,
          rangeEnd,
          timezone,
          granularity,
          unit
        });
      }));
    });
  }

  getLastNDays({
    /*: { days: number }*/

    days = 7
  } = {})
  /*: Promise<Stats>*/
  {
    const _this4 = this;

    return _call(function () {
      if (days === Infinity) {
        const today = dateFns.startOfMonth(dateFns.addMonths(new Date(), 1)); // beginning of _next_ month

        const startDate = dateFns.startOfMonth(dateFns.subMonths(today, 12 * 8)); // ~8 years

        const granularity = 1;
        const unit = 'month';
        return _await(_this4.getDateRange({
          startDate: getDatePart(startDate),
          endDate: getDatePart(today),
          granularity,
          unit
        }), function (stats) {
          return stats.trimStart();
        });
      } else {
        // eslint-disable-line no-else-return
        const today = dateFns.addDays(new Date(), 1);
        const startDate = dateFns.subDays(today, days);
        const granularity = getIntervalSize(days);
        return _await(_this4.getDateRange({
          startDate: getDatePart(startDate),
          endDate: getDatePart(today),
          granularity
        }));
      }

      return _await();
    });
  }

  getLoyalty({
    /*: {
        rangeStart: isoDateString,
        rangeEnd: isoDateString,
        granularity?: number,
        timezone: ?string,
        unit?: string,
        context?: ?Object
      }*/

    rangeStart,
    rangeEnd,
    granularity = 1,
    timezone,
    unit = 'day',
    context
  })
  /*: Promise<Stats>*/
  {
    const _this5 = this;

    return _call(function () {
      return _await(_this5.canAccessUnrestricted(true), function (canAccessUnrestricted) {
        const path = `${_this5.buildPath(context)}/loyalty${canAccessUnrestricted ? '/unrestricted' : ''}`;
        const params = {
          rangeStart,
          rangeEnd,
          granularity,
          timezone,
          unit
        };
        const req = new _this5.client.Request(path, params, loyaltyFields);
        req.parse = parse;
        return _this5.client.get(req);
      });
    });
  }

  getLoyaltyDateRange({
    /*: {
        startDate: string,
        endDate: string,
        granularity?: number,
        unit?: string
      }*/

    startDate,
    endDate,
    granularity,
    unit = 'day'
  }) {
    const _this6 = this;

    return _call(function () {
      let _exit2 = false;

      const {
        propertyId,
        businessId,
        locationId
      } = _this6.context.serialize();

      const offerId = _this6.offerId;
      const timezone
      /*: ?string*/
      = getUserTimezone();
      const rangeStart = getExplicitDateCrossover(startDate, unit);
      const rangeEnd = getExplicitDateCrossover(endDate, unit);
      return _await(_invoke(function () {
        if (!locationId && businessId) {
          return _await(_this6.hasAllLocations({
            propertyId,
            businessId,
            offerId
          }), function (hasAllLocations) {
            return function () {
              if (!hasAllLocations) {
                return _await(_this6.client.properties.for(propertyId).businesses.for(businessId).locations.search({
                  hasRoles: true,
                  accessContext: 'implicit'
                }), function (myLocations) {
                  return _await(Promise.all(myLocations.items.map(eachLocation => _this6.getLoyalty({
                    rangeStart,
                    rangeEnd,
                    timezone,
                    granularity,
                    unit,
                    context: {
                      propertyId,
                      businessId,
                      locationId: eachLocation.id,
                      offerId
                    }
                  }).catch(() => null))), function (allStats) {
                    // Ignore any accesses we have for locations that don't exist =\
                    _exit2 = true;
                    return allStats.filter(Boolean).reduce((acc, value) => value.add(acc));
                  });
                });
              }
            }();
          });
        }
      }, function (_result3) {
        return _exit2 ? _result3 : _this6.getLoyalty({
          rangeStart,
          rangeEnd,
          timezone,
          granularity,
          unit
        });
      }));
    });
  }

  getLoyaltyLastNDays({
    /*: { days: number }*/

    days = 7
  } = {})
  /*: Promise<Stats>*/
  {
    const _this7 = this;

    return _call(function () {
      if (days === Infinity) {
        const today = dateFns.startOfMonth(dateFns.addMonths(new Date(), 1)); // beginning of _next_ month

        const startDate = dateFns.startOfMonth(dateFns.subMonths(today, 12 * 8)); // ~8 years

        const granularity = 1;
        const unit = 'month';
        return _await(_this7.getLoyaltyDateRange({
          startDate: getDatePart(startDate),
          endDate: getDatePart(today),
          granularity,
          unit
        }), function (stats) {
          return stats.trimStart();
        });
      } else {
        // eslint-disable-line no-else-return
        const today = dateFns.addDays(new Date(), 1);
        const startDate = dateFns.subDays(today, days);
        const granularity = getIntervalSize(days);
        return _await(_this7.getLoyaltyDateRange({
          startDate: getDatePart(startDate),
          endDate: getDatePart(today),
          granularity
        }));
      }

      return _await();
    });
  }

  getLoyaltyUniques()
  /*: Promise<StatsUnique>*/
  {
    const path = `${this.buildPath()}/uniques/loyalty`;
    const req = new this.client.Request(path, {}, uniquesFields);
    req.parse = StatsUnique.fromJson;
    return this.client.get(req);
  }

  tailEvents(integrationId
  /*: string*/
  , previous
  /*: boolean*/
  = false)
  /*: Promise<Object>*/
  {
    const {
      propertyId,
      businessId
    } = this.context.serialize();
    const path = propertyId ? `/properties/${propertyId}/integrations/${integrationId}/events` : `/businesses/${businessId}/integrations/${integrationId}/events`;
    const eventFields = ['events', 'warnings', propertyId ? 'propertyId' : 'businessId', 'integrationId', 'interval'];
    const req = new this.client.Request(path, {
      previous
    }, eventFields);
    return this.client.get(req);
  }

  getEvent(integrationId
  /*: string*/
  , eventKey
  /*: string*/
  )
  /*: Promise<Object>*/
  {
    const {
      propertyId,
      businessId
    } = this.context.serialize();
    const path = propertyId ? `/properties/${propertyId}/integrations/${integrationId}/events/${encodeURIComponent(eventKey)}` : `/businesses/${businessId}/integrations/${integrationId}/events/${encodeURIComponent(eventKey)}`;
    const req = new this.client.Request(path, {}, []);
    return this.client.get(req);
  }

}

function getExplicitDateCrossover(dateString, unit) {
  const [yyyy, mm, dd] = dateString.split('-');
  const date = new Date();
  date.setFullYear(yyyy, mm - 1, dd);
  const endDate = unit === 'month' ? dateFns.startOfMonth(date) : dateFns.startOfDay(date);
  return endDate.toISOString();
}

function getDatePart(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getIntervalSize(days
/*: number*/
)
/*: number*/
{
  /* TODO: Calculate an hour interval where
   * a) rangeInDays % interval === 0
   * b) rangeInDays / interval <= 96
   *
   * Currently, this is based on the assumption that we'll only ever get `days` inputs of 7, 30, 60, 90, or 364 (7*52)
   */
  // Assumes unit is always "days"
  if (days <= 96) return 1;
  return Math.ceil(Math.ceil(days / 96) / 7) * 7;
}

function getUserTimezone() {
  let timeZone = jstz.determine().name();

  if (Intl) {
    const dateTime = Intl.DateTimeFormat(); // IE 11 does not support '.resolvedOptions()' on 'Intl.DateTimeFormat()'

    timeZone = dateTime && dateTime.resolvedOptions() && dateTime.resolvedOptions().timeZone ? dateTime.resolvedOptions().timeZone : timeZone;
  }

  return timeZone;
}

module.exports = StatsRepo;